/**
 * 商品閲覧履歴機能 - localStorage ベースの実装
 * お気に入り機能と同様のデザインシステムに準拠
 */

class BrowsingHistoryManager {
  constructor() {
    this.storageKey = 'shopify_browsing_history';
    this.maxItems = 20;
    this.history = this.loadHistory();
  }

  loadHistory() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load browsing history:', e);
      return [];
    }
  }

  saveHistory() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.history));
    } catch (e) {
      console.error('Failed to save browsing history:', e);
    }
  }

  addProduct(product) {
    if (!product || !product.id) return;

    // 既存のエントリを削除（重複防止）
    this.history = this.history.filter(item => item.id !== product.id);

    // 先頭に追加
    this.history.unshift({
      id: product.id,
      handle: product.handle,
      title: product.title,
      image: product.image,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      url: product.url,
      viewedAt: new Date().toISOString()
    });

    // 最大件数を超えたら古いものを削除
    if (this.history.length > this.maxItems) {
      this.history = this.history.slice(0, this.maxItems);
    }

    this.saveHistory();
  }

  getHistory() {
    return this.history;
  }

  getCount() {
    return this.history.length;
  }

  clearHistory() {
    this.history = [];
    this.saveHistory();
  }
}

// 閲覧履歴セクション
class BrowsingHistorySection extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.emptyMessage = null;
    this.itemsContainer = null;
  }

  connectedCallback() {
    this.container = this.querySelector('.browsing-history__container');
    this.emptyMessage = this.querySelector('.browsing-history__empty');
    this.itemsContainer = this.querySelector('.browsing-history__items');
    this.renderItems();
  }

  renderItems() {
    if (!window.browsingHistoryManager) return;

    const history = window.browsingHistoryManager.getHistory();

    if (history.length === 0) {
      if (this.itemsContainer) this.itemsContainer.innerHTML = '';
      if (this.emptyMessage) this.emptyMessage.classList.remove('hidden');
      return;
    }

    if (this.emptyMessage) this.emptyMessage.classList.add('hidden');

    if (this.itemsContainer) {
      this.itemsContainer.innerHTML = history.map((item, index) => `
        <li class="browsing-history__item grid__item${this.dataset.animateOnScroll === 'true' ? ' scroll-trigger animate--slide-in' : ''}"
            ${this.dataset.animateOnScroll === 'true' ? `data-cascade style="--animation-order: ${index + 1};"` : ''}>
          <div class="card-wrapper product-card-wrapper underline-links-hover">
            <div class="card card--standard card--media">
              <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: 100%;">
                <div class="card__media">
                  <div class="media media--transparent media--hover-effect">
                    <a href="${item.url}" class="full-unstyled-link">
                      ${item.image ? `
                        <img
                          src="${item.image}"
                          alt="${this.escapeHtml(item.title)}"
                          loading="lazy"
                          width="300"
                          height="300"
                          class="motion-reduce"
                        >
                      ` : `
                        <div class="browsing-history__placeholder">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                        </div>
                      `}
                    </a>
                  </div>
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading h5">
                    <a href="${item.url}" class="full-unstyled-link">
                      ${this.escapeHtml(item.title)}
                    </a>
                  </h3>
                  <div class="card-information">
                    <div class="price">
                      <div class="price__container">
                        <div class="price__regular">
                          ${item.compareAtPrice && item.compareAtPrice !== item.price ? `
                            <span>
                              <s class="price-item price-item--regular">${item.compareAtPrice}</s>
                            </span>
                          ` : ''}
                          <span class="price-item price-item--regular">${item.price}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
      `).join('');
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// グローバルインスタンスを作成
document.addEventListener('DOMContentLoaded', () => {
  window.browsingHistoryManager = new BrowsingHistoryManager();
});

// カスタム要素を登録
customElements.define('browsing-history-section', BrowsingHistorySection);
